import { forwardRef, ReactNode, Ref } from "react";
import { mergeRefs } from "@react-aria/utils";
import cx from "classnames";

import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import useTaskCreate from "shared/Stack/useTaskCreate";
import Tooltip from "ds/components/Tooltip";
import { TooltipReferenceProps } from "ds/components/Tooltip/types";
import Button from "ds/components/Button";

type PerformTaskButtonProps = {
  children: ReactNode;
  stackId?: string;
  command?: string;
  className?: string;
  skipInitialization?: boolean;
  disabled?: boolean;
  variant?: "primary" | "secondary";
  tooltip?: string;
  setNextUrl: (nextUrl: string) => void;
};

const PerformTaskButton = forwardRef<HTMLButtonElement, PerformTaskButtonProps>(
  function PerformTaskButton(
    {
      children,
      stackId,
      command,
      className,
      skipInitialization = false,
      disabled,
      variant = "primary",
      tooltip,
      setNextUrl,
    }: PerformTaskButtonProps,
    ref
  ) {
    const { reportSuccess } = useTypedContext(FlashContext);

    const [createTask, { loading }] = useTaskCreate();

    const performTask = () => {
      if (stackId && command) {
        createTask({ stack: stackId, command, skipInitialization }, (data) => {
          if (data?.taskCreate.id) {
            const taskId = data.taskCreate.id;
            reportSuccess({ message: `Task ${taskId} successfully triggered` });
            setNextUrl(`/stack/${stackId}/run/${taskId}`);
          }
        });
      }
    };

    return (
      <Tooltip
        active={!!tooltip}
        on={(props: TooltipReferenceProps) => (
          <Button
            {...props}
            disabled={!stackId || !command || disabled}
            loading={loading}
            className={cx(className, props.className)}
            onPress={performTask}
            ref={mergeRefs(props.ref, ref) as Ref<HTMLButtonElement>}
            variant={variant}
          >
            {children}
          </Button>
        )}
      >
        {tooltip}
      </Tooltip>
    );
  }
);

export default PerformTaskButton;
